export type Placement = {
  xM: number;
  yM: number;
  widthM: number;
  depthM: number;
  orientation: number;
};

export type PlannedPlacement = {
  rack_id: number;
  target: { x_m: number; y_m: number; rotation: number; row_id: string | null; rack_pos_in_row: number | null };
};

export type FloorPlanning = {
  placements: PlannedPlacement[];
  rack_reservations: Array<{ id: string; rack_id: number; reason: string }>;
};

export type PlacementSource = {
  x_m?: number | null;
  y_m?: number | null;
  width_m?: number | null;
  depth_m?: number | null;
  rotation?: number | null;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  widthM?: number | null;
  depthM?: number | null;
  orientation?: number;
};

const DEFAULT_WIDTH_M = 0.6;
const DEFAULT_DEPTH_M = 1.0;

export function normalizeOrientation(value: number | null | undefined): number {
  const raw = Number(value ?? 0);
  if (!Number.isFinite(raw)) return 0;
  const normalized = ((raw % 360) + 360) % 360;
  return Math.round(normalized * 1000) / 1000;
}

export function placementFromSource(source: PlacementSource, ppm = 100): Placement {
  return {
    xM: source.x_m ?? (source.x != null ? source.x / ppm : 0),
    yM: source.y_m ?? (source.y != null ? source.y / ppm : 0),
    widthM: source.width_m ?? source.widthM ?? (source.width != null ? source.width / ppm : DEFAULT_WIDTH_M),
    depthM: source.depth_m ?? source.depthM ?? (source.height != null ? source.height / ppm : DEFAULT_DEPTH_M),
    orientation: normalizeOrientation(source.orientation ?? source.rotation),
  };
}

export function placementToPersisted(placement: Placement): {
  x_m: number;
  y_m: number;
  width_m: number;
  depth_m: number;
  rotation: number;
} {
  const millimetres = (metres: number) => Math.round(metres * 1000) / 1000;
  return {
    x_m: millimetres(placement.xM),
    y_m: millimetres(placement.yM),
    width_m: millimetres(placement.widthM),
    depth_m: millimetres(placement.depthM),
    rotation: placement.orientation,
  };
}

export function placementToPixels(placement: Placement, ppm = 100) {
  return {
    x: Math.round(placement.xM * ppm),
    y: Math.round(placement.yM * ppm),
    width: Math.round(placement.widthM * ppm),
    height: Math.round(placement.depthM * ppm),
    rotation: placement.orientation,
  };
}
